import type { ExportDressingOptions } from '../schemas/export.js';
import { AVG_CHAR_WIDTH_FACTOR } from './estimate-row-heights.js';
import { CSS_PX_PER_PT, LINE_HEIGHT_FACTOR } from './page-geometry.js';

export const HEADER_TITLE_PT = 14;
export const HEADER_META_PT = 8;
export const HEADER_MAX_TITLE_LINES = 2;
export const HEADER_VERTICAL_PADDING_PX = 18;
export const HEADER_LOGO_HEIGHT_PX = 40;
/** The dates block always reserves its two lines (« Exporté le » and « Version du »). */
export const HEADER_DATE_LINES = 2;

export function titleLineCount(title: string, contentWidthPx: number): number {
  const fontPx = HEADER_TITLE_PT * CSS_PX_PER_PT;
  const charsPerLine = Math.max(10, Math.floor(contentWidthPx / (fontPx * AVG_CHAR_WIDTH_FACTOR)));
  return Math.min(HEADER_MAX_TITLE_LINES, Math.max(1, Math.ceil(title.length / charsPerLine)));
}

/**
 * The document header's pinned height: the tallest of its three flex items —
 * logo, title block (title lines + the company line under them) and dates
 * block — plus the bottom padding. Each item counts only when it is drawn, so
 * a header carrying dates alone is as tall as two meta lines, not as tall as a
 * title nobody renders (chantier 1 carry-over: `showDates` was accepted and
 * never read, and the title block was always counted).
 */
export function documentHeaderHeightPx(input: {
  title: string;
  contentWidthPx: number;
  showTitle: boolean;
  showLogo: boolean;
  showDates: boolean;
  enabled: boolean;
}): number {
  if (!input.enabled) return 0;
  const titleLinePx = Math.ceil(HEADER_TITLE_PT * CSS_PX_PER_PT * LINE_HEIGHT_FACTOR);
  const metaLinePx = Math.ceil(HEADER_META_PT * CSS_PX_PER_PT * LINE_HEIGHT_FACTOR);
  const logoHeight = input.showLogo ? HEADER_LOGO_HEIGHT_PX : 0;
  const titleHeight = input.showTitle
    ? titleLineCount(input.title, input.contentWidthPx) * titleLinePx + metaLinePx
    : 0;
  const datesHeight = input.showDates ? HEADER_DATE_LINES * metaLinePx : 0;
  return Math.max(logoHeight, titleHeight, datesHeight) + HEADER_VERTICAL_PADDING_PX;
}

/**
 * Single source of truth for whether the document header's title/logo/dates
 * sub-elements are visible, and therefore whether the header block itself is
 * `enabled` at all (spec D1/D4). `dressing.titleBlock` alone does not gate the
 * header: a document with the title block off but a company logo or dates
 * still draws a (smaller) header carrying just those.
 *
 * Both `render-export-html.ts` (the renderer, which decides what to actually
 * draw) and `planPdfDocument` (which reserves tiling space for exactly what
 * the renderer will draw, via `documentHeaderHeightPx` above) must derive
 * `showTitle`/`showLogo`/`showDates`/`enabled` from this one function —
 * export-rework chantier 1 Task 9's review found the route re-deriving
 * `enabled` on its own (as `dressing.titleBlock` alone) and drifting from the
 * renderer whenever the title block was off but a logo or dates were on,
 * under-reserving the tiling budget for a header the renderer draws anyway.
 *
 * Takes whether a logo is available rather than its bytes, so a caller that
 * only knows the render facts (the browser, re-planning a PDF the server
 * drew) asks exactly the question the renderer asks.
 */
export function documentHeaderVisibility(input: {
  dressing: Pick<ExportDressingOptions, 'titleBlock' | 'companyLogo' | 'dates'>;
  /** `false` means "no logo to show" — whether because the company has none or its fetch failed. */
  hasCompanyLogo: boolean;
  /** Pre-formatted display dates, or `null` when there is nothing to show for that line — see `RenderExportHtmlInput`. */
  exportedAt: string | null;
  matrixUpdatedAt: string | null;
}): { showTitle: boolean; showLogo: boolean; showDates: boolean; enabled: boolean } {
  const showTitle = input.dressing.titleBlock;
  const showLogo = input.dressing.companyLogo && input.hasCompanyLogo;
  const showDates =
    input.dressing.dates && (input.exportedAt !== null || input.matrixUpdatedAt !== null);
  return { showTitle, showLogo, showDates, enabled: showTitle || showLogo || showDates };
}

/**
 * Whether the document draws a footer at all (spec R4) — lifted out of the
 * renderer so the tiling can reserve its height (`FOOTER_RESERVED_PX`) under
 * exactly the condition the renderer draws it. A blank footer text is no
 * footer text; in assembly-marks (Atelier) mode the signature leaves the
 * footer for the last page's corner, so a signature alone no longer keeps the
 * footer alive there.
 */
export function documentFooterEnabled(
  dressing: Pick<
    ExportDressingOptions,
    'footerText' | 'pageNumbers' | 'pmsSignature' | 'assemblyMarks'
  >,
): boolean {
  const hasFooterText = dressing.footerText !== null && dressing.footerText.trim().length > 0;
  return (
    hasFooterText ||
    dressing.pageNumbers ||
    (dressing.pmsSignature !== 'aucune' && !dressing.assemblyMarks)
  );
}
